import { useState, useEffect } from "react"
import {
  PaymentMethod,
  RecurringDonation,
  Designation,
  RecurringDonationStatus,
} from "../types"
import { formattedCents } from "source/giving/utils/money"
import { Heading, Button } from "@planningcenter/doxy-web"
import { usePaymentMethodsContext } from "../hooks/usePaymentMethodsContext"
import { useQuery } from "@tanstack/react-query"
import { fetchFunds } from "../queries"
import PaymentMethodSelector from "./PaymentMethodSelector"
import EditDesignations from "./EditDesignations"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { includedDesignationsToData } from "../queries"
import { AlertMessage } from "source/shared/components"
import DeleteConfirmationDialog from "source/giving/profile/components/DeleteConfirmationDialog"
import EditSchedule from "./EditSchedule"
import { Schedule } from "source/giving/donate/form/types"
import { getInitialStatus, scheduleStateToAttributes } from "../utils/frequency"

interface EditRecurringDonationFormProps {
  recurringDonation: RecurringDonation
  donationTotalAmount: number
  selectedPaymentMethod: PaymentMethod | null
  onCancel: () => void
  onSave: (donation: RecurringDonation) => void
  onDelete: (deletedDonation: RecurringDonation) => void
}

export default function EditRecurringDonationForm({
  recurringDonation,
  donationTotalAmount,
  selectedPaymentMethod,
  onCancel,
  onSave,
  onDelete,
}: EditRecurringDonationFormProps) {
  const { paymentMethods } = usePaymentMethodsContext()
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [showDialog, setShowDialog] = useState(false)

  const {
    data: funds,
    isLoading: isLoadingFunds,
    isError: isErrorFunds,
  } = useQuery({
    queryKey: ["Funds"],
    queryFn: () => fetchFunds(),
  })
  const [designations, setDesignations] = useState<Designation[]>(
    recurringDonation.designations,
  )

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(selectedPaymentMethod?.id ?? recurringDonation.payment_method_id)

  useEffect(() => {
    if (selectedPaymentMethod !== null) {
      setSelectedPaymentMethodId(selectedPaymentMethod.id)
    }
  }, [selectedPaymentMethod])

  const [schedule, setSchedule] = useState<Schedule>(recurringDonation.schedule)
  const [status, setStatus] = useState<RecurringDonationStatus>(
    getInitialStatus(recurringDonation),
  )
  const [releaseHoldAt, setReleaseHoldAt] = useState<string | null>(
    recurringDonation.release_hold_at,
  )
  const [nextOccurrence, setNextOccurrence] = useState<string | null>(
    recurringDonation.next_occurrence,
  )

  const handleChangeDesignations = (designations: Designation[]) => {
    setDesignations(designations)
  }

  const headingText = `${formattedCents(donationTotalAmount, { noCentsIfWhole: true })} ${recurringDonation.humanized_schedule}`

  const handleAddPaymentMethodClick = () => {
    // TODO: implement
  }

  async function handleDeleteClick() {
    setShowDialog(false)
    setIsSaving(true)
    setHasErrors(false)

    try {
      await sessionApiClient.del(
        `/giving/v2/me/recurring_donations/${recurringDonation.id}`,
      )
      onDelete(recurringDonation)
      onCancel()
    } catch (error) {
      setHasErrors(true)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelClick = () => {
    onCancel()
  }

  async function handleUpdateClick() {
    setIsSaving(true)
    setHasErrors(false)

    scheduleStateToAttributes(
      recurringDonation,
      schedule,
      status,
      releaseHoldAt,
      nextOccurrence,
    )
    const included = includedDesignationsToData(designations)
    const requestPayload = {
      data: {
        attributes: {
          release_hold_at: recurringDonation.release_hold_at,
          on_hold_indefinitely: recurringDonation.on_hold_indefinitely,
          schedule: recurringDonation.schedule,
          next_occurrence: recurringDonation.next_occurrence,
        },
        relationships: {
          payment_method: {
            data: { type: "PaymentMethod", id: selectedPaymentMethodId },
          },
        },
      },
      included: included,
    }

    try {
      await sessionApiClient.patch(
        `/giving/v2/me/recurring_donations/${recurringDonation.id}`,
        requestPayload,
      )
      onSave(recurringDonation)
    } catch (error) {
      setHasErrors(true)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div>
      <header className="mb-2">
        <Heading level={3} size={4} text="Manage recurring donation" />
        <p className="mt-4p c-tint2">{headingText}</p>
      </header>
      {hasErrors && (
        <AlertMessage type="error" icon="general#exclamation-triangle">
          Something went wrong. Please try again.
        </AlertMessage>
      )}
      {!isLoadingFunds && !isErrorFunds && funds && (
        <>
          <EditDesignations
            designations={designations}
            funds={funds}
            onChangeDesignations={handleChangeDesignations}
          />
          <PaymentMethodSelector
            onAddClick={handleAddPaymentMethodClick}
            onChange={(id: string | null) => setSelectedPaymentMethodId(id)}
            paymentMethods={paymentMethods}
            selectedId={selectedPaymentMethodId}
          />
          <EditSchedule
            schedule={schedule}
            status={status}
            releaseHoldAt={releaseHoldAt}
            nextOccurrence={nextOccurrence}
            onChangeFrequency={(frequency: Schedule) => setSchedule(frequency)}
            onChangeStatus={(status: RecurringDonationStatus) =>
              setStatus(status)
            }
            onChangeReleaseHoldAt={(releaseHoldAt: string | null) =>
              setReleaseHoldAt(releaseHoldAt)
            }
            onChangeNextOccurrence={(nextOccurrence: string | null) =>
              setNextOccurrence(nextOccurrence)
            }
          />
        </>
      )}
      <div className="mt-2 d-f ai-c jc-sb">
        <Button
          onClick={() => setShowDialog(true)}
          text="Delete"
          theme="destroy"
          size="md"
          disabled={isSaving}
        />
        <div className="d-f g-1">
          <Button
            onClick={handleCancelClick}
            text="Cancel"
            variant="naked"
            size="md"
            disabled={isSaving}
          />
          <Button
            onClick={handleUpdateClick}
            text="Update donation"
            size="md"
            disabled={isSaving}
          />
        </div>
      </div>

      {showDialog && (
        <DeleteConfirmationDialog
          label="Delete recurring donation?"
          description="This will remove all scheduled occurrences of this donation. If you change your mind, you'll have to create a new recurring donation."
          deleteButtonText="Delete"
          onCancel={() => setShowDialog(false)}
          onDelete={() => handleDeleteClick()}
        />
      )}
    </div>
  )
}
